/*
 * Advanced Search Layout Panel Template
 *
 * Includes:
 * - Two-panel layout for advanced search
 * - Results panel (left)
 * - Details panel (right)
 */
import type {
  PanelArea,
  PanelsBreakpointTemplate,
  PanelsTemplate,
  SearchPanelArea,
} from '../types';
import { DETAILS_DEFAULT_WIDTH, DETAILS_MIN_WIDTH } from './constants';

const DETAILS_TABLET_SIZE = 340;
const DETAILS_TABLET_MAX = 400;
const DETAILS_TABLET_MIN = 200;

/**
 * Base template (Mobile)
 * Shows only results panel on mobile — details is collapsed via showing: false.
 * All panels must be present in areas so they have a grid cell; omitting them causes overlapping.
 */
export function baseTemplate(): PanelsBreakpointTemplate {
  return {
    areas: [['results', 'details']],
    resizers: {},
    columns: [
      {
        key: 'results',
        defaultMin: 300,
        defaultSize: '1fr',
        isToggleable: false,
        showing: true,
      },
      {
        key: 'details',
        defaultMin: DETAILS_MIN_WIDTH,
        defaultSize: DETAILS_DEFAULT_WIDTH,
        isToggleable: false,
        showing: false,
      },
    ],
    rows: [
      {
        key: 'defaultRow',
        defaultMin: 200,
        defaultSize: '1fr',
        isToggleable: false,
        showing: true,
      },
    ],
  };
}

/**
 * Tablet template
 * Shows results and details.
 */
export function tabletTemplate(
  __visible: Array<PanelArea>
): PanelsBreakpointTemplate {
  return {
    areas: [['results', 'details']],
    resizers: {
      results: 'right',
    },
    columns: [
      {
        key: 'results',
        defaultMin: 300,
        defaultSize: '1fr',
        isToggleable: false,
        showing: true,
      },
      {
        key: 'details',
        defaultMin: DETAILS_TABLET_MIN,
        defaultSize: DETAILS_TABLET_SIZE,
        defaultMax: DETAILS_TABLET_MAX,
        isToggleable: false,
        showing: true,
      },
    ],
    rows: [
      {
        key: 'defaultRow',
        defaultMin: 200,
        defaultSize: '1fr',
        isToggleable: false,
        showing: true,
      },
    ],
  };
}

/**
 * Desktop template
 * Shows results and details.
 */
export function desktopTemplate(
  __visible: Array<PanelArea>
): PanelsBreakpointTemplate {
  return {
    areas: [['results', 'details']],
    resizers: {
      results: 'right',
    },
    columns: [
      {
        key: 'results',
        defaultMin: 300,
        defaultSize: '1fr',
        isToggleable: false,
        showing: true,
      },
      {
        key: 'details',
        defaultMin: DETAILS_MIN_WIDTH,
        defaultSize: DETAILS_DEFAULT_WIDTH,
        defaultMax: 600,
        isToggleable: false,
        showing: true,
      },
    ],
    rows: [
      {
        key: 'defaultRow',
        defaultMin: 200,
        defaultSize: '1fr',
        isToggleable: false,
        showing: true,
      },
    ],
  };
}

export function getPanelState(visible: Array<PanelArea>): PanelsTemplate {
  return {
    base: baseTemplate(),
    tablet: tabletTemplate(visible),
    desktop: desktopTemplate(visible),
    visible,
  };
}

export const getDefaultVisiblePanels = (): Array<SearchPanelArea> => {
  return ['results', 'details'];
};

export const getDefaultPanelState = (): PanelsTemplate => {
  return getPanelState(getDefaultVisiblePanels());
};
